import {useTranslation} from 'react-i18next';
import TextField from 'web-library/stories/atom/TextField/TextField';
import Box from 'web-library/stories/atom/Box/Box';
import Grid from 'web-library/stories/atom/Grid/Grid';
import Accordion from 'web-library/stories/atom/Accordion/Accordion';
import DatePicker from 'web-library/stories/atom/DatePicker/DatePicker';
import {DateOnlySemanticDisplayFormat} from 'web-library';
import React, {useState} from 'react';
import {Dayjs} from "dayjs";

import {makeStyles} from 'tss-react/mui';
import {UseFormReturn} from 'react-hook-form';
import {Hidden, useMediaQuery} from '@mui/material';
import {
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../logic/useCreateCandidateForm';
import {useGetIExamMasterCode} from '../../../../api-refactored/user/useGetIExamMasterCode';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../../models';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import {IExamMasterCodeItem, LookupListApiItem} from '../../../../api/user/model';
import {usePrCitizenshipForm} from '../../logic/usePrCitizenshipForm';

import {typeTheme} from "web-library/theme/helper/tssHelper"
// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
    ({
      errorAsterisk: {
        color: theme.palette.error.main,
      },
      textField: {
        paddingBottom: theme.spacing(3),
      },
      textFieldSmallPaddingBottom: {
        paddingBottom: theme.spacing(1),
      },
      BoxWithRadioButton: {
        paddingBottom: theme.spacing(3),
      },
      radioButton: {
        paddingBottom: theme.spacing(2),
      },
      groupMultirowContainer: {
        width: '100%',
        marginBottom: theme.spacing(1),
      },
      groupFormElementContainer: {
        marginBottom: theme.spacing(1),
        width: '100%',
      },
      responsiveFlexContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
      },
      formElementContainer: {
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('md')]: {
          marginBottom: theme.spacing(3),
        },
        [theme.breakpoints.down('sm')]: {
          marginBottom: theme.spacing(2),
        },
        width: '100%',
      },
      textFieldLabel: {
        color: theme.color.text.textHeader,
        ...typeTheme(theme, 'inputLabel'),
        marginBottom: theme.spacing(1),
      },
      textFieldLabelDisabled: {
        color: theme.color.text.textDisabled,
        ...typeTheme(theme, 'inputLabel'),
        marginBottom: theme.spacing(1),
      },
      getAddressButton: {
        width: 'calc(100% - 166px)',
        paddingRight: theme.spacing(1),
        display: 'inline-flex',
        [theme.breakpoints.down('md')]: {
          width: '100%',
          paddingRight: theme.spacing(0),
        },
      },
    }));

const defaultDOBPickerProps = {
  placeholder: 'Enter nationality issue date',
  disableFuture: true,
  format: DateOnlySemanticDisplayFormat,
};

export interface PersonalParticularsProps {
  formHook: UseFormReturn;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  profileData?: CandidateProfileFormData;
  isSingPassUser?: boolean;
  isIExamsApiEnabled?: boolean;
}

const PersonalParticulars = ({
                               formHook,
                               formDefaultValues,
                               isDisableInputs,
                               profileData,
                               isSingPassUser = false,
                               isIExamsApiEnabled = true,
                             }: PersonalParticularsProps) => {
  const {t} = useTranslation();
  const {classes} = useStyles();
  const {control, formState: {errors}, register, setValue} = formHook;

  const [nationalityIssueDate, setNationalityIssueDate] = useState<string>('');
  const {
    data: dataIEXAM,
    isLoading: isLoadingIEXAM,
    error: errorMasterCode,
    refetch: refetchMasterCode,
  } = useGetIExamMasterCode({enabled: isIExamsApiEnabled});

  const prCitizenHook = usePrCitizenshipForm({
    formHook,
    isDisableInputs,
    formDefaultValues,
    profileData,
    isSingPassUser,
    isIExamsApiEnabled,
  });
  const hidden = useMediaQuery(theme => theme.breakpoints.up('xs'));
  return (
      <Accordion
          header={t('profile->>CreateCandidateForm.PersonalParticulars')}
          defaultExpanded
          bottomLine
      >
        {!hidden &&
            <TextField
                isInputTextUpperCase
                onChange={(e) => setValue('hanyupinyinName', e.target.value?.toString().toUpperCase())}
                inputRef={register}
                label={t('profile->>CreateCandidateForm.HanyuPinyinName')}
                placeholder="Enter hanyu pinyin name"
                name="hanyupinyinName"
                type="none"
                id="input-hanyupinyinName"
                error={!!errors.hanyupinyinName}
                helperText={errors?.hanyupinyinName?.message?.toString()}
                fullWidth
                className={classes.textField}
                disabled={isDisableInputs?.hanyupinyinName}
                InputLabelProps={{
                  disabled: isDisableInputs?.hanyupinyinName,
                }}
                inputProps={{
                  readOnly: isDisableInputs?.hanyupinyinName,
                }}
            />
        }
        <Box className={classes.textField}>
          <ControlledAutocomplete
              label={t('profile->>CreateCandidateForm.Gender')}
              isRequired
              control={control}
              name="gender"
              id="input-select-gender"
              error={!!errors?.gender}
              helperText={errors?.gender?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={dataIEXAM ? dataIEXAM.gender : []}
              getOptionLabel={(option) => typeof option === 'string' ? option : getIExamDropdownItemLabel(option)}
              placeholder="Select sex"
              disabled={isDisableInputs?.gender}
              isOptionEqualToValue={getIExamMasterCodeSelected}
          />
        </Box>
        <Box className={classes.textField}>
          <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={t('profile->>CreateCandidateForm.Race')}
              isRequired
              control={control}
              name="race"
              id="input-select-race"
              error={!!errors.race}
              helperText={errors?.race?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              getOptionLabel={getIExamDropdownItemLabel}
              options={dataIEXAM ? dataIEXAM.race : []}
              placeholder="Select race"
              disabled={isDisableInputs?.race}
              isOptionEqualToValue={getIExamMasterCodeSelected}
          />
        </Box>
        <Grid container spacing={4} direction="row" className={classes.textField}>
          <Grid md={12} sm={12} xs={12}>
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
                label={t('profile->>CreateCandidateForm.Nationality')}
                isRequired
                control={control}
                name="nationality"
                id="input-select-nationality"
                options={dataIEXAM ? dataIEXAM.nationality : []}
                getOptionLabel={getIExamDropdownItemLabel}
                error={!!errors.nationality}
                helperText={errors?.nationality?.message?.toString()}
                loading={isLoadingIEXAM}
                loadingError={!!errorMasterCode}
                loadingErrorOnReFetch={refetchMasterCode}
                placeholder="Select nationality"
                disabled={isDisableInputs?.nationality}
                isOptionEqualToValue={getIExamMasterCodeSelected}
            />
          </Grid>
          <Hidden xsUp>
            <Grid md={6} sm={12} xs={12}>
              <DatePicker
                  label={<>{t('profile->>CreateCandidateForm.NationalityIssueDate')}</>}
                  // formValue={formDefaultValues?.nationalityIssueDate || nationalityIssueDate}
                  onChange={(date) => {
                    setNationalityIssueDate(date || '');
                    setValue('nationalityIssueDate', date || '');
                  }}
                  inputVariant="filled"
                  name="nationalityIssueDate"
                  inputRef={(e) => register("nationalityIssueDate").ref(e)}
                  error={!!errors.nationalityIssueDate}
                  helperText={errors?.nationalityIssueDate?.message?.toString()}
                  disabled={isDisableInputs?.nationalityIssueDate}
                  {...defaultDOBPickerProps}
              />
            </Grid>
          </Hidden>
        </Grid>
        <Box className={classes.textField}>
          <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={t('profile->>CreateCandidateForm.PrStatus')}
              isRequired
              control={control}
              name="prStatus"
              id="input-select-prStatus"
              error={!!errors?.prStatus}
              helperText={errors?.prStatus?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={prCitizenHook?.prStatusOption || []}
              getOptionLabel={getIExamDropdownItemLabel}
              placeholder="Select PR status"
              disabled={isDisableInputs?.prStatus || prCitizenHook?.isForeignerSetting}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              value={prCitizenHook?.prStatusState}
              onChange={prCitizenHook?.onPrStatusChange}
          />
        </Box>
        <Box className={classes.textField}>
          <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={t('profile->>CreateCandidateForm.CitizenshipType')}
              isRequired
              control={control}
              name="citizenship"
              id="input-select-citizenship"
              error={!!errors.citizenship}
              helperText={errors?.citizenship?.message?.toString()}
              loading={isLoadingIEXAM}
              loadingError={!!errorMasterCode}
              loadingErrorOnReFetch={refetchMasterCode}
              options={dataIEXAM ? dataIEXAM.citizenship : []}
              getOptionLabel={getIExamDropdownItemLabel}
              placeholder="Select citizenship"
              disabled={isDisableInputs?.citizenship}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              value={prCitizenHook?.citizenshipState}
              onChange={(_, newValue) => {
                prCitizenHook?.setCitizenship(newValue);
              }}
          />
        </Box>
        <Hidden xsUp>
          <Box className={classes.textField}>
            <ControlledAutocomplete<LookupListApiItem, false, false, undefined>
                label={t('profile->>CreateCandidateForm.Occupation')}
                control={control}
                name="occupation"
                id="input-select-occupation"
                error={!!errors.occupation}
                helperText={errors?.occupation?.message?.toString()}
                loading={isLoadingIEXAM}
                loadingError={!!errorMasterCode}
                loadingErrorOnReFetch={refetchMasterCode}
                options={dataIEXAM ? dataIEXAM.occupation : []}
                getOptionLabel={(name: LookupListApiItem) => name.description}
                placeholder="Enter occupation"
                disabled={isDisableInputs?.occupation}
                isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
            />
          </Box>
        </Hidden>
      </Accordion>
  );
};

export default PersonalParticulars;
